import moment from "moment";

import { AppointmentCountItem } from "@/Appointments/Api/Dto/Responses/AppointmentCountItem";
import { PredefinedAppointment } from "@/Appointments/Api/Dto/Responses/PredefinedAppointment";
import { PredefinedRuleSet } from "@/Appointments/Api/Dto/Responses/PredefinedRuleSet";
import { AppointmentCreateData } from "@/Appointments/Models/Appointment/AppointmentCreateDataComposite";
import { PredefinedRuleSetModel } from "@/Appointments/Models/Scheduling/PredefinedRuleSetModel";
import { CalendarStore } from "@/Appointments/Stores/CalendarStore";
import { Appointment as MyleModelsResponseCalendarAppointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { INotificationContactPoint } from "@/Common/Api/Contacts/Dto/INotificationContactPoint";
import { RealTimeNotificationResourceType } from "@/Common/Api/RealTimeNotification/Dto/RealTimeNotificationResourceType";
import { realTimeNotificationClient } from "@/Common/Api/RealTimeNotification/SignalRClient";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { ImportableAppointmentPatientSnapshot } from "@/Common/Models/Appointments/AppointmentPatientSnapshot";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { ImportableThirdPartyPatient } from "@/Common/Models/Patient/ThirdPartyPatient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { Person } from "@/Common/Models/Person/Person";
import { PatientProfileMyleDto } from "@/Common/Models/Portal/Patient/SharedPatientMyleDto";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent, ServerEventWithRequest, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { Dispatcher } from "@/Common/Stores/Dispatcher";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";

class CalendarResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);
        this.RegisterOperation(ServerOperations.Server.Myle_SetRecallToCompleted, this.setRecallToCompleted);
        this.RegisterOperation(ServerOperations.Server.Myle_CreatePatient, this.createPatient);
        this.RegisterOperation(ServerOperations.Server.Myle_SearchAppointments, this.searchAppointmentsMod, SubServerOperations.Calendar.SearchAppointments_ModificationReload);
        this.RegisterOperation(ServerOperations.Server.Myle_SearchAppointments, this.searchAppointmentsGen, SubServerOperations.Calendar.SearchAppointments_GeneralReload);
        this.RegisterOperation(ServerOperations.Server.Myle_Appointment_Counts, this.appointmentCounts);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadAppointment, this.loadAppointment);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_LoadPredefinedAppointment, this.loadPredefinedAppointment);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadPatient, this.loadPatient);
        this.RegisterOperation(ServerOperations.Server.Myle_Appointment_ChangeStatus, this.changeStatus);
        this.RegisterOperation(ServerOperations.Server.Myle_UpdateAppointment, this.updateAppointment);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_CreateAppointment, this.createAppointment);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_DeletePredefinedAppointment, this.deletePredefinedAppointment);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_CheckDoubleBooking, this.checkDoubleBooking);
        this.RegisterOperation(ServerOperations.Server.Myle_Appointment_FindWorkingClinicians, this.findWorkingClinicians);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_GenerateAppointmentsPDF, this.generateAppointmentsPDF);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_GenerateAppointmentConfirmationPDF, this.showPdfPrintPreview);
        this.RegisterOperation(ServerOperations.Server.Myle_AppointmentCertificate_GeneratePdf, this.showPdfPrintPreview);
        this.RegisterOperation(ServerOperations.Server.Myle_AppointmentConfirmation_AppointmentCertificate_GenerateMergePdf, this.showPdfPrintPreview);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_Save_Search_Config, this.saveSearchConfig);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_GetRuleset, this.rulesetLoaded, SubServerOperations.Calendar.LoadRuleset);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_CheckRulesetValidity, this.checkRulesetValidity);
        this.RegisterOperation(ServerOperations.Server.Portal_Proxy_GetPatientProfile, this.loadedPortalPatientProfile);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_DeleteSinglePredefinedAppointmentOrRuleSet, this.refreshAfterDeletingRuleset);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_UpdateRuleSet, this.updateRuleSetAfterSaveOrUpdate);
        this.RegisterOperation(ServerOperations.Server.RAMQ_CheckSubscription, this.loadRamqInfo);
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_LoadAppointmentThirdPartyPatientInfo, this.loadAppointmentExternalPatientInfo);
        this.RegisterOperation(ServerOperations.Server.Myle_AppointmentCertificate_GeneratePdf, this.showPdfPrintPreview);
    }

    private saveSearchConfig(response: ServerEvent): void {
        var displayResponseSetting = response.Data.AppointmentSearchSettings;
        if (displayResponseSetting) {
            cacheStore.UserSettings.CalendarDisplay = displayResponseSetting;
        }
    }

    private setRecallToCompleted(response: ServerEvent, store: CalendarStore): void {
        store.calendarActions.LoadPatient(store.appointmentEditModel);
    }

    private createPatient(response: ServerEvent, store: CalendarStore): void {
        var contactId = response.Data["PatientContactId"];
        var individual = response.Data["Individual"];

        // this will set selected patient to newly created
        store.appointmentEditModel = store.appointmentEditModel.set("SelectedPatientId", contactId);
        if (individual) {
            var patients = Immutable.List([Person.FromIndividual(individual)]);
            store.appointmentEditModel = store.appointmentEditModel.set("SelectedPatient", patients);
        }

        store.calendarActions.LoadPatient(store.appointmentEditModel);
    }

    private searchAppointmentsMod(response: ServerEvent, store: CalendarStore): void {
        if (response.hasErrors())
            return;

        var appointments: MyleModelsResponseCalendarAppointment[] = response.Data["Appointments"];
        var predefinedAppointments: PredefinedAppointment[] = response.Data["PredefinedAppointments"];
        var predefinedRuleSets: PredefinedRuleSet[] = response.Data["PredefinedScheduleRuleSet"];

        store.calendarModel = store.calendarModel.set("LastLoad", new Date());
        store.ReloadCalendarForEmployee(appointments?.map(x => MyleModelsResponseCalendarAppointment.FromJsonResponseObject(x)), predefinedAppointments, predefinedRuleSets);

        const providers = store.calendarModel.get("DisplayedEmployees").toArray().map(employee => employee.ContactId);
        sessionStore.dispatch(LocalOperations.Sidebar.Sidebar_LoadNotice_Counts);

        realTimeNotificationClient.updateChannelsByProviders(RealTimeNotificationResourceType.Appointment, providers);
    }

    private searchAppointmentsGen(response: ServerEvent, store: CalendarStore): void {
        const appointments: MyleModelsResponseCalendarAppointment[] = response.Data["Appointments"];
        const predefinedAppointments: PredefinedAppointment[] = response.Data["PredefinedAppointments"];
        const predefinedRuleSets: PredefinedRuleSet[] = response.Data["PredefinedScheduleRuleSet"];

        store.calendarModel = store.calendarModel.set("LastLoad", new Date());
        store.calendarModel = store.calendarModel.set("Appointments", Immutable.List<MyleModelsResponseCalendarAppointment>(appointments?.map(x => MyleModelsResponseCalendarAppointment.FromJsonResponseObject(x))));
        store.calendarModel = store.calendarModel.set("PredefinedAppointments", Immutable.List<PredefinedAppointment>(predefinedAppointments));
        store.calendarModel = store.calendarModel.set("PredefinedRuleSets", Immutable.List<PredefinedRuleSet>(predefinedRuleSets));

        store.SetCalendarAppointments();

        const providers = store.calendarModel.get("DisplayedEmployees").toArray().map(employee => employee.ContactId);
        sessionStore.dispatch(LocalOperations.Sidebar.Sidebar_LoadNotice_Counts);

        realTimeNotificationClient.updateChannelsByProviders(RealTimeNotificationResourceType.Appointment, providers);
    }

    private appointmentCounts(response: ServerEvent, store: CalendarStore): void {
        const appointmentCounts: AppointmentCountItem[] = response.Data["AppointmentCountItems"];
        store.setCalendarAppointmentCounts(appointmentCounts);
    }

    private loadAppointment(response: ServerEvent, store: CalendarStore): void {
        let appointment: MyleModelsResponseCalendarAppointment = MyleModelsResponseCalendarAppointment.FromJsonResponseObject(response.Data["Appointment"]);

        if (appointment) {
            let calendarAppointmentEditModel = store.GetAppointmentEditModel(appointment);
            let patient = Patient.FromLoadAppointment(appointment);

            store.SetRecurrenceTypes();
            calendarAppointmentEditModel = calendarAppointmentEditModel.set("RecurrenceTypes", Immutable.List(store.recurrenceTypes));

            store.appointmentEditModel = calendarAppointmentEditModel;

            let appointments = store.calendarModel.get("AppointmentsDisplay").toArray();
            appointments = appointments.mutate(
                p => {
                    p.PatientContactId = patient.IndividualResponse.Contact_id;
                    p.PortalDisplayClass = "";
                },
                p => p.AppointmentId === appointment.AppointmentId && !!patient.IndividualResponse?.Contact_id
            );

            store.calendarModel = store.calendarModel
                .set("AppointmentsDisplay", Immutable.List(appointments))
                .set("IsEditAppointmentDialogVisible", true);

            this.setContactPointNotifications(patient, appointment.HasNotificationContactPointSpecified);

            const patientBannerModel = {
                Model: null,
                PatientDropdownVisible: false,
                Warnings: []
            };

            store.patientBannerModel = Immutable
                .fromJS(patientBannerModel)
                .set("Model", patient);

            store.refreshNotifyPatientStatus();
        }
    }

    private loadPredefinedAppointment(response: ServerEvent, store: CalendarStore): void {
        if (response.Data["PredefinedAppointment"] != null) {
            var predefinedAppointment: PredefinedAppointment = PredefinedAppointment.FromJsonResponseObject(response.Data["PredefinedAppointment"]);
            if (predefinedAppointment) {
                store.appointmentEditModel = store.GetPredefinedAppointmentEditModel(predefinedAppointment);

                store.SetRecurrenceTypes();
                store.appointmentEditModel = store.appointmentEditModel.set("RecurrenceTypes", Immutable.List(store.recurrenceTypes));

                store.calendarModel = store.calendarModel.set("IsEditAppointmentDialogVisible", true);

                store.refreshNotifyPatientStatus();
            }
        }
        else {
            store.calendarModel = store.calendarModel.set("OpenAlreadyBookedPopup", true);
        }
    }

    private loadPatient(response: ServerEvent, store: CalendarStore): void {
        let patient = Patient.FromResponse(response);
        let banner: IPatientBannerModel = null;

        if (store.patientBannerModel != null) {
            banner = store.patientBannerModel
                .set("Model", null)
                .set("PatientDropdownVisible", false)
                .set("Warnings", [].toImmutable());
        }
        else {
            let bannerModel = {
                Model: null,
                PatientDropdownVisible: false,
                Warnings: []
            };

            banner = Immutable.fromJS(bannerModel);
        }

        store.appointmentEditModel = store.appointmentEditModel
            .set("SelectedPatientId", patient.IndividualResponse.Contact_id)
            .set("SelectedPatient", Immutable.List([Person.FromPatient(patient)]));

        banner = banner.set("Model", patient);
        store.patientBannerModel = banner;

        // Contains check for teleconsultation and notification checkbox enabled/disabled
        store.refreshNotifyPatientStatus();

        let hasNotificationContactPointSpecified = store.appointmentEditModel.get("HasNotificationContactPointSpecified");
        if (store.appointmentEditModel.get("IsNewAppointment") && !store.appointmentEditModel.get("NotifyPatient"))
            hasNotificationContactPointSpecified = false;

        this.setContactPointNotifications(patient, hasNotificationContactPointSpecified);

        banner = banner.set("Model", patient);
        store.patientBannerModel = banner;

        store.calendarActions.loadRamqInfo(patient);
    }

    private changeStatus(response: ServerEvent, store: CalendarStore): void {
        var intervenorsIds = response.Data["AffectedEmployees"];
        store.calendarActions.ReloadCalendarForEmployee(intervenorsIds, store.calendarModel, store.calendarDisplaySettingsModel);
    }

    private updateAppointment(response: ServerEventWithRequest<{ Appointment: MyleModelsResponseCalendarAppointment, UserHasAccessToPatientFile: boolean }>, store: CalendarStore): void {
        const appointment = store.appointmentEditModel.get("Appointment");
        store.calendarModel = store.calendarModel.set("IsEditAppointmentDialogVisible", false);

        if (appointment.IsPatientNotificationsEnabled) {
            patientStore.ActionsProfile.updatePatientProfileNotifications(
                store.appointmentEditModel.get("SelectedPatientId"),
                response.requestDto.Appointment.ContactPointNotificationList
            );
        }
        this.handlePrint(response, store);

        store.isSaving = false;
        sessionStore.hideGlobalOverlay();
        calendarStore.clearPatientToPreloadOnNextAppCreation();

        this.redirectToPatientFileOrReload(store, response.Data?.UserHasAccessToPatientFile);
    }

    private createAppointment(response: ServerEventWithRequest<{ Appointments: AppointmentCreateData[], UserHasAccessToPatientFile: boolean }>, store: CalendarStore): void {
        store.calendarModel = store.calendarModel.set("IsEditAppointmentDialogVisible", false);
        for (let appointment of (response.requestDto.Appointments?.filter(p => p.IsPatientNotificationsEnabled) ?? [])) {
            patientStore.ActionsProfile.updatePatientProfileNotifications(appointment.PatientContactId, appointment.ContactPointNotificationList);
        }
        this.handlePrint(response, store);

        store.isSaving = false;
        sessionStore.hideGlobalOverlay();
        calendarStore.clearPatientToPreloadOnNextAppCreation();

        this.redirectToPatientFileOrReload(store, response.Data?.UserHasAccessToPatientFile);
    }

    private redirectToPatientFileOrReload(store: CalendarStore, hasAccess: boolean): void {
        if (store.calendarModel.get("OpenPatientProfileAfterSave") && !hasAccess) {
            sessionStore.showPopup({
                title: cacheStore.GetString("Allowlist_RestrictedAccessLabel"),
                content: cacheStore.GetString("Allowlist_PatientFileAccessDeniedMessage"),
                showCancel: true,
                showOk: false,
                showCloseX: true
            });
        }

        if (store.calendarModel.get("OpenPatientProfileAfterSave") && hasAccess) {
            const patientId = store.appointmentEditModel.get("SelectedPatientId");
            window.setTimeout(() => {
                var operation = new LocalEvent(LocalOperations.Misc.RedirectToTab);
                operation.AddItem("currentLocation", RouterHistory.location);
                operation.AddItem("value", patientId);
                operation.AddItem("target", RedirectTo.Patient);
                LocalEventDispatcher.dispatch(operation);
                store.calendarModel = store.calendarModel.set("OpenPatientProfileAfterSave", false);
            }, 10);
        }
        else {
            LocalEvent.Dispatch(LocalOperations.Calendar.Calendar_Refresh_Click);
        }
        // Reset models after save completes to avoid stale data issues
        store.SetDefaultEditAppointmentModel();
        store.SetDefaultPatientModel();
        store.SetDefaultPatientBannerModel();
        store.refreshNotifyPatientStatus();
    }

    private handlePrint(response: ServerEvent, store: CalendarStore): void {
        if (store.appointmentEditModel.get("IsCalendarEventTab"))
            return;

        if (!store.appointmentEditModel.get("IsPrintLabelOnSave") && !store.appointmentEditModel.get("IsPrintCertificateOnSave"))
            return;

        const appointment = store.appointmentEditModel.get("Appointment");
        const appointmentDate = store.appointmentEditModel.get("StartTime");
        const siteId = store.appointmentEditModel.get("SelectedSiteId");

        const patientName = store.GetPatientNameForPrinting();
        const patientContactId = store.appointmentEditModel.get("SelectedPatient").first()?.ContactId;
        const patientProfileId = appointment.IsSharedWithPortal ? appointment.ExternalId : null;

        if (store.appointmentEditModel.get("IsPrintLabelOnSave") && store.appointmentEditModel.get("IsPrintCertificateOnSave")) {
            store.calendarActions.PrintMergeAppointmentConfirmationCertificate(
                store.appointmentEditModel.get("AppointmentIntervenor")?.ContactFacetId,
                patientName,
                patientContactId,
                patientProfileId,
                appointmentDate,
                siteId
            );
        }
        else if (store.appointmentEditModel.get("IsPrintLabelOnSave")) {
            store.calendarActions.PrintAppointmentLabel(store.appointmentEditModel.get("AppointmentIntervenor")?.ContactFacetId, patientName, appointmentDate, siteId);
        }
        else if (store.appointmentEditModel.get("IsPrintCertificateOnSave")) {
            store.calendarActions.PrintAppointmentCertificate(patientContactId, patientProfileId, appointmentDate, siteId, store.appointmentEditModel.get("PatientSearchString"));
        }
    }

    private deletePredefinedAppointment(response: ServerEvent, store: CalendarStore): void {
        const intervenorsId: Guid | null = response.Data["DeletedPredefinedAppointmentIntervenorId"];

        if (intervenorsId)
            store.calendarActions.ReloadCalendarForEmployee([intervenorsId], store.calendarModel, store.calendarDisplaySettingsModel);
        else
            LocalEvent.Dispatch(LocalOperations.Calendar.Calendar_Refresh_Click);
    }

    private checkDoubleBooking(response: ServerEvent, store: CalendarStore): void {
        var isDoubleBooked: boolean = response.Data["IsResourceDoubleBooked"];

        store.calendarModel = store.calendarModel.set("IsEditAppointmentDialogVisible", false);

        if (isDoubleBooked) {
            // appointment is double booked :  show an alert dialog
            store.calendarModel = store.calendarModel.set("IsDoubleBookingDialogVisible", true);
            sessionStore.showGlobalOverlay(true);
        }
        else {
            store.calendarActions.SaveAppointment(store);
        }
    }

    private findWorkingClinicians(response: ServerEvent, store: CalendarStore): void {
        const displayedEmployees = store.calendarModel.get("DisplayedEmployees");
        var clinicianIds: any[] = response.Data["Clinicians"];
        var workingClinicians: EmployeeUIModel[] = [];

        console.log(displayedEmployees);
        console.log(clinicianIds);

        clinicianIds.toImmutable().groupBy(x => x.EmployeeId).forEach((items, EmployeeId) => {
            var employee = cacheStore.GetEmployeeByContactFacetId(EmployeeId);
            var employeeToPush = EmployeeUIModel.fromCompositeObject(employee);
            employeeToPush.Target = CheckboxTarget.Myle_Calendar_PrintByDoctor_EmployeeItem;
            employeeToPush.Sites = items.map(s => s.SiteId).toArray();
            employeeToPush.Checked = displayedEmployees.map(({ ContactFacetId }) => ContactFacetId).includes(EmployeeId);

            workingClinicians.push(employeeToPush);
        });

        workingClinicians = workingClinicians.filter((x: EmployeeUIModel) => x.Model.IsInactive == false);

        const isAllEmployeesChecked = workingClinicians.every(x => x.Checked);

        var Sites: SiteUIModel[] = [];
        clinicianIds.map(s => s.SiteId).distinct().forEach(SiteId => {
            var site = cacheStore.GetSiteById(SiteId);
            var model = SiteUIModel.FromCompositeObject(site);
            model.Target = CheckboxTarget.Myle_Calendar_PrintByDoctor_SiteItem;
            model.Checked = true;
            Sites.push(model);
        });

        store.calendarPrintByDoctorModel = store.calendarPrintByDoctorModel.set("EmployeesGlobalCache", workingClinicians.toImmutable());
        store.calendarPrintByDoctorModel = store.calendarPrintByDoctorModel.set("EmployeesFiltered", workingClinicians.toImmutable());

        store.calendarPrintByDoctorModel = store.calendarPrintByDoctorModel.set("Sites", store.helper.ChangeCheckStateFromAllICheckable(Immutable.List(Sites), true));
        store.calendarPrintByDoctorModel = store.calendarPrintByDoctorModel.set("IsAllSitesChecked", true);
        store.calendarPrintByDoctorModel = store.calendarPrintByDoctorModel.set("IsAllEmployeesChecked", isAllEmployeesChecked);

        store.calendarModel = store.calendarModel.set("IsPrintMDDialogVisible", true);
    }

    private generateAppointmentsPDF(response: ServerEvent, store: CalendarStore): void {
        PrintHelper.ShowPrintPreviewFileName(response.Data["FileName"]);

        store.calendarModel = store.calendarModel.set("IsPrintMDDialogVisible", false);
        store.calendarModel = store.calendarModel.set("IsPrintCalendarDialogVisible", false);
    }

    private showPdfPrintPreview(response: ServerEvent): void {
        PrintHelper.ShowPrintPreviewFileName(response.Data["FileName"]);
    }

    private rulesetLoaded(response: ServerEvent, store: CalendarStore): void {
        var rulesets = response.Data.Rulesets;

        if (rulesets != null && rulesets.length > 0) {
            // set ruleset in model
            var rulesetModel = PredefinedRuleSetModel.New();
            rulesetModel.Model = rulesets[0];
            var comboServiceList = rulesetModel.ToComboServiceList();

            let startDate = moment(rulesetModel.Model.StartDate);
            let endDate = moment(rulesetModel.Model.EndDate);

            store.calendarRulesetModel = store.calendarRulesetModel.set("SelectedRuleset", rulesetModel);
            store.calendarRulesetModel = store.calendarRulesetModel.set("RulesetForEdit", comboServiceList.toImmutable());
            store.calendarRulesetModel = store.calendarRulesetModel.set("StartDate", startDate.toDate());
            store.calendarRulesetModel = store.calendarRulesetModel.set("EndDate", endDate.toDate());

            // set ruleset edit popup display
            store.calendarRulesetModel = store.calendarRulesetModel.set("IsRulesetPopupOpen", true);
            sessionStore.showGlobalOverlay(true);
        }
    }

    private checkRulesetValidity(response: ServerEventWithThen<any, (isValid: boolean) => void>): void {
        if (response.then != null)
            response.then(response.Data.IsValid);
    }

    private loadedPortalPatientProfile(response: ServerEvent, store: CalendarStore): void {
        // check if in loading mode to not execute following logic if a "load patient profile" call took a while to execute and is returned out of context
        if (store.appointmentEditModel.get("IsLoadingPatientProfileFromPortal")) {
            var portalPatient: PatientProfileMyleDto = response.Data.PatientProfile;

            store.patientPortalModel = Person.FromPortalPatientProfile(portalPatient);
            store.appointmentEditModel = store.appointmentEditModel.set("IsLoadingPatientProfileFromPortal", false);
        }
    }

    private refreshAfterDeletingRuleset(response: ServerEvent): void {
        if (response.Data["Success"] === true) {
            LocalEvent.Dispatch(LocalOperations.Calendar.Calendar_Refresh_Click);
        }
        else
            sessionStore.showPopup(response.Errors);
    }

    private updateRuleSetAfterSaveOrUpdate(response: ServerEvent): void {
        let success = response.Data["Success"];

        if (!success) {
            let existingRulesetDates = response.Data["ExistingRulesetDates"];
            if (existingRulesetDates && existingRulesetDates.length > 0) {
                Dispatcher.dispatch(LocalOperations.Calendar.Ruleset_OpenRulesetCollisionPopup, { existingRulesetDates: existingRulesetDates });
            }
        }
        else {
            sessionStore.hideGlobalOverlay();
            Dispatcher.dispatch(LocalOperations.Calendar.Ruleset_CloseRulesetCollisionPopup, { closeRulesetPopup: true });
        }
    }

    private setContactPointNotifications(patient: Patient, hasNotificationContactPointSpecified: boolean = false): void {
        if (!patient)
            return;

        let contactPointNotificationIds = calendarStore.appointmentEditModel.get("ContactPointNotificationList")?.toArray();

        if (contactPointNotificationIds.length == 0 && !hasNotificationContactPointSpecified)
            calendarStore.setNotificationsByNotifyPatientStatus(!!calendarStore.appointmentEditModel.get("Questionnaire") || calendarStore.appointmentEditModel.get("NotifyPatient"));
        else {
            this.setContactIsSelectedForNotification(patient.Phones, contactPointNotificationIds);
            this.setContactIsSelectedForNotification(patient.Emails, contactPointNotificationIds);

            patient.Relationships
                ?.forEach(p => {
                    this.setContactIsSelectedForNotification(p.PhoneNumbers, contactPointNotificationIds);
                    this.setContactIsSelectedForNotification(p.Emails, contactPointNotificationIds);
                });
        }
    }

    private setContactIsSelectedForNotification(contacts: INotificationContactPoint[], contactPointNotificationIds: string[]): void {
        contacts?.forEach(contact => contact.isSelectedForNotification = contactPointNotificationIds.contains(contact.ContactPoint_id));
    }

    private loadRamqInfo(response: ServerEvent, store: CalendarStore): void {
        let data = new RAMQInfo().FromResponseData(response.Data);
        let patient = store.patientBannerModel.get("Model");

        let banner = store.patientBannerModel
            .set("Model", null);

        if (patient)
            patient.setRamq(data);

        store.patientBannerModel = banner.set("Model", patient);
    }

    private loadAppointmentExternalPatientInfo(response: ServerEvent, store: CalendarStore): void {
        const thirdPartyPatient = new ImportableThirdPartyPatient();
        const patientSnapshot = new ImportableAppointmentPatientSnapshot();

        Object.assign(thirdPartyPatient, response.Data["ThirdPartyPatient"]);
        Object.assign(patientSnapshot, response.Data["PatientSnapshot"]);

        thirdPartyPatient.AppointmentContactData = patientSnapshot.ContactData;
        store.appointmentEditModel = store.appointmentEditModel
            .set("IsLoadingThirdPartyPatientData", false)
            .set("ThirdPartyPatient", thirdPartyPatient)
            .set("PatientSnapshot", patientSnapshot);
    }
}

export { CalendarResponseHandler };
